import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Slider,
  Typography,
  CircularProgress,
  IconButton,
  Alert
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';

import { fetchRecentImages, loadClusteringSettings, saveClusteringSettings } from '../services/api/faceGroupingService';

// Client-side clustering algorithm (mirrors backend implementation)
const clusterFacesBySpatialProximity = (faceDetails, config) => {
  const SIZE_SIMILARITY_THRESHOLD = config.size_similarity_threshold || 0.5;
  const PROXIMITY_DISTANCE_THRESHOLD = config.proximity_distance_threshold || 0.4;
  const MIN_FACE_CONFIDENCE = config.min_face_confidence || 90;

  if (!faceDetails || faceDetails.length === 0) {
    return [];
  }

  // Filter by confidence
  const filteredFaces = faceDetails.filter(face => face.Confidence >= MIN_FACE_CONFIDENCE);

  if (filteredFaces.length === 0) {
    return [];
  }

  // Calculate metrics
  const faceMetrics = filteredFaces.map((face, index) => {
    const bbox = face.BoundingBox;
    const area = bbox.Width * bbox.Height;
    const centroidX = bbox.Left + (bbox.Width / 2);
    const centroidY = bbox.Top + (bbox.Height / 2);

    return {
      face: face,
      area: area,
      centroidX: centroidX,
      centroidY: centroidY,
      originalIndex: index
    };
  });

  // Sort by area (largest first)
  const sorted = [...faceMetrics].sort((a, b) => b.area - a.area);

  // Greedy clustering
  const clusters = [];
  const visited = new Set();

  for (let i = 0; i < sorted.length; i++) {
    if (visited.has(i)) continue;

    const seedFace = sorted[i];
    const cluster = {
      id: `cluster-${i}`,
      faces: [seedFace],
      seedArea: seedFace.area,
      seedCentroid: {
        x: seedFace.centroidX,
        y: seedFace.centroidY
      }
    };
    visited.add(i);

    // Find similar + close faces
    for (let j = i + 1; j < sorted.length; j++) {
      if (visited.has(j)) continue;

      const candidateFace = sorted[j];

      // Check size similarity
      const sizeRatio = candidateFace.area / seedFace.area;
      const sizeSimilar = (sizeRatio >= SIZE_SIMILARITY_THRESHOLD);

      if (!sizeSimilar) {
        continue;
      }

      // Check spatial proximity
      const dx = candidateFace.centroidX - seedFace.centroidX;
      const dy = candidateFace.centroidY - seedFace.centroidY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const proximate = (distance < PROXIMITY_DISTANCE_THRESHOLD);

      if (proximate) {
        cluster.faces.push(candidateFace);
        visited.add(j);
      }
    }

    clusters.push(cluster);
  }

  // Calculate cluster stats
  clusters.forEach(cluster => {
    cluster.avgArea = cluster.faces.reduce((sum, f) => sum + f.area, 0) / cluster.faces.length;
    cluster.avgCentroid = {
      x: cluster.faces.reduce((sum, f) => sum + f.centroidX, 0) / cluster.faces.length,
      y: cluster.faces.reduce((sum, f) => sum + f.centroidY, 0) / cluster.faces.length
    };
    cluster.faceCount = cluster.faces.length;
  });

  // Sort by avgArea and assign metadata
  const sortedClusters = [...clusters].sort((a, b) => b.avgArea - a.avgArea);
  const largestArea = sortedClusters[0].avgArea;

  sortedClusters.forEach((cluster, index) => {
    cluster.index = index;
    cluster.relativeSize = cluster.avgArea / largestArea;
    cluster.description = `Cluster ${index} (${cluster.faceCount} face${cluster.faceCount !== 1 ? 's' : ''}, ${(cluster.relativeSize * 100).toFixed(0)}% relative size)`;
  });

  return sortedClusters;
};

const CLUSTER_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DFE6E9', // Gray
  '#A29BFE', // Purple
  '#FD79A8', // Pink
  '#FDCB6E', // Orange
  '#6C5CE7'  // Indigo
];

/**
 * FaceGroupingTester - UI for testing face grouping settings
 *
 * Allows admins to:
 * - View last 20 images with face detections
 * - Adjust grouping parameters (size similarity, proximity distance)
 * - See real-time visualization of how groups change
 * - Save settings or reset to defaults
 * - Paginate through images
 */
export default function FaceGroupingTester() {
  // Settings state
  const [settings, setSettings] = useState({
    size_similarity_threshold: 0.5,
    proximity_distance_threshold: 0.4,
    min_face_confidence: 90
  });

  // Saved settings (for reset functionality)
  const [savedSettings, setSavedSettings] = useState({
    size_similarity_threshold: 0.5,
    proximity_distance_threshold: 0.4,
    min_face_confidence: 90
  });

  // Data state
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Load images on mount and page change
  useEffect(() => {
    loadImages();
  }, [page]);

  // Load saved settings on mount
  useEffect(() => {
    loadSavedSettings();
  }, []);

  const loadImages = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchRecentImages({ page, limit: 20 });
      setImages(response.images);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Error loading images:', err);
      setError('Failed to load images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedSettings = async () => {
    try {
      const response = await loadClusteringSettings();
      setSavedSettings(response);
      setSettings(response);
    } catch (err) {
      console.error('Error loading saved settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await saveClusteringSettings(settings);
      setSavedSettings({ ...settings });
      alert('Settings saved successfully! These will be used for future image processing.');
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = () => {
    setSettings({ ...savedSettings });
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  // Compute clusters for all images with current settings
  const imagesWithClusters = useMemo(() => {
    return images.map(image => ({
      ...image,
      clusters: clusterFacesBySpatialProximity(image.faceDetails, settings)
    }));
  }, [images, settings]);

  const hasUnsavedChanges =
    settings.size_similarity_threshold !== savedSettings.size_similarity_threshold ||
    settings.proximity_distance_threshold !== savedSettings.proximity_distance_threshold ||
    settings.min_face_confidence !== savedSettings.min_face_confidence;

  return (
    <Box>
      {/* Compact Settings Bar */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Size Similarity */}
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
              Size: {settings.size_similarity_threshold.toFixed(2)}
            </Typography>
            <Slider
              value={settings.size_similarity_threshold}
              onChange={(e, value) => setSettings({ ...settings, size_similarity_threshold: value })}
              min={0}
              max={1}
              step={0.05}
              size="small"
              valueLabelDisplay="auto"
            />
          </Grid>

          {/* Proximity Distance */}
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
              Distance: {settings.proximity_distance_threshold.toFixed(2)}
            </Typography>
            <Slider
              value={settings.proximity_distance_threshold}
              onChange={(e, value) => setSettings({ ...settings, proximity_distance_threshold: value })}
              min={0}
              max={1.5}
              step={0.05}
              size="small"
              valueLabelDisplay="auto"
            />
          </Grid>

          {/* Min Confidence */}
          <Grid item xs={12} sm={2}>
            <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
              Min: {settings.min_face_confidence}%
            </Typography>
            <Slider
              value={settings.min_face_confidence}
              onChange={(e, value) => setSettings({ ...settings, min_face_confidence: value })}
              min={50}
              max={100}
              step={5}
              size="small"
              valueLabelDisplay="auto"
            />
          </Grid>

          {/* Pagination */}
          <Grid item xs={6} sm={2}>
            <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
              Page {page + 1}/{totalPages}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" onClick={handlePreviousPage} disabled={page === 0}>
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleNextPage} disabled={page >= totalPages - 1}>
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={6} sm={2}>
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
              <IconButton
                size="small"
                onClick={handleResetSettings}
                disabled={!hasUnsavedChanges}
                title="Reset to saved"
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color={hasUnsavedChanges ? "warning" : "default"}
                onClick={handleSaveSettings}
                disabled={saving || !hasUnsavedChanges}
                title="Save settings"
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Image Grid */}
      {!loading && (
        <Grid container spacing={2}>
          {imagesWithClusters.map((image) => (
            <Grid item xs={12} sm={6} md={4} key={image.imageId}>
              <ImageCard image={image} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

/**
 * ImageCard - Displays a single image with bounding boxes and cluster info
 */
function ImageCard({ image }) {
  const canvasRef = React.useRef(null);
  const imageRef = React.useRef(null);

  const drawBoundingBoxes = () => {
    if (!image.clusters || image.clusters.length === 0) return;
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const img = imageRef.current;

    // Wait for image to load
    if (img.width === 0 || img.height === 0) return;

    const ctx = canvas.getContext('2d');

    // Set canvas size to match displayed image
    canvas.width = img.width;
    canvas.height = img.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw bounding boxes for each cluster
    image.clusters.forEach((cluster, clusterIndex) => {
      const color = CLUSTER_COLORS[clusterIndex % CLUSTER_COLORS.length];
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;

      cluster.faces.forEach((faceMetric) => {
        const bbox = faceMetric.face.BoundingBox;

        // AWS Rekognition bounding box coordinates are ratios (0-1)
        const x = bbox.Left * canvas.width;
        const y = bbox.Top * canvas.height;
        const width = bbox.Width * canvas.width;
        const height = bbox.Height * canvas.height;

        ctx.strokeRect(x, y, width, height);
      });
    });
  };

  React.useEffect(() => {
    drawBoundingBoxes();
  }, [image.clusters]);

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 0.5 }}>
        {/* Image with Canvas Overlay */}
        <Box sx={{ position: 'relative', display: 'inline-block', width: '100%', mb: 0.5 }}>
          <img
            ref={imageRef}
            src={image.url}
            alt={`Detection ${image.imageId}`}
            onLoad={drawBoundingBoxes}
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
          />
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none'
            }}
          />
        </Box>

        {/* Cluster Info */}
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
          {image.clusters.map((cluster, index) => (
            <Box
              component="span"
              key={cluster.id}
              sx={{
                color: CLUSTER_COLORS[index % CLUSTER_COLORS.length],
                fontWeight: 'bold',
                mr: 0.5
              }}
            >
              C{index}:{cluster.faceCount}
            </Box>
          ))}
        </Typography>
      </CardContent>
    </Card>
  );
}
